import { Period } from "revlock-webutils";
import FullMonthRatablePlan from "./plan/FullMonthRatablePlan";
import Modified30_360Plan from "./plan/Modified30_360Plan";
import FixedMonthRatablePlan from "./plan/FixedMonthRatablePlan";
import DailyRatablePlan from "./plan/DailyRatablePlan";
import ApportionedRatablePlan from "./plan/ApportionedRatablePlan";
import True30_360Plan from "./plan/True30_360Plan";
import _ from "lodash";

/**
 * Validate that the given plan is valid
 *
 * @param plan
 */
export function assertValidPlan(plan) {
    for (let p of plan) {
        if (!p || !p.actgPeriod || isNaN(p.planAmount)) {
            throw new Error("Created an invalid plan");
        }
    }
}

/**
 * Validate that the provided revenue arrangement is valid
 *
 * @param ra
 * @param type
 */
export function assertValidArrangement(ra, type) {
    for (let item of ra.revenueArrangementItem) {
        if (item.plan) {
            if (!Array.isArray(item.plan)) {
                throw new Error("No valid plan");
            }
            for (let p of item.plan) {
                if (!p.period && !p.actgPeriod) {
                    throw new Error("Invalid Revenue Arrangement");
                }

                if (type === "w") {
                    if (p.actgPeriod) {
                        throw new Error("Invalid Revenue Arrangement");
                    }

                    if (!p.period) {
                        throw new Error("Invalid Revenue Arrangement");
                    }
                }
                if (type === "r") {
                    if (!p.actgPeriod?.period) {
                        throw new Error("Invalid Revenue Arrangement");
                    }
                }
            }
        }
    }
}

export function getAutoCreateSSP(orgConfig) {
    const autoCreateSSPConfig = orgConfig.find(
        (config) => config.id === "properties/auto-create-ssp"
    );
    const autoCreateSSP =
        (autoCreateSSPConfig && autoCreateSSPConfig.value) || false;

    return autoCreateSSP;
}

export function getEndPeriodUnPlannedAmount(
    plan,
    startDate,
    endDate,
    actualServiceEndDate,
    orgConfig,
    calendarConfig
) {
    const d2 = orgConfig.find(
        (config) => config.id === "properties/ratable_end_date_inclusive"
    );
    const endDateInclusive = (d2 && d2.value) || false;

    if (startDate == endDate) {
        // same day, no revenue
        return 0;
    }

    const startActgPeriod = Period.toActgPeriod(startDate, calendarConfig);
    const endActgPeriod = Period.toActgPeriod(endDate, calendarConfig);

    let totalPlanAmount = 0;
    // STEP 1: Calculate total plan amount
    plan.forEach((p) => {
        totalPlanAmount += p.planAmount;
    });

    // STEP 2: Calculate total plan amount with new end date
    if (startActgPeriod.period === endActgPeriod.period) {
        // write off in same month as service start
        const revenueDays = Period.daysBetween(
            startDate,
            actualServiceEndDate,
            true,
            endDateInclusive
        );
        const perDayRevenue = totalPlanAmount / revenueDays;
        const newRevenueDays = Period.daysBetween(
            startDate,
            endDate,
            true,
            endDateInclusive
        );
        const planNewAmount = perDayRevenue * newRevenueDays;

        return totalPlanAmount - planNewAmount;
    }

    let totalNewPlanAmount = 0;
    const actualEndActgPeriod = Period.toActgPeriod(
        actualServiceEndDate,
        calendarConfig
    );
    for (const p of plan) {
        // we will only iterate until last period
        if (p.actgPeriod.period === endActgPeriod.period) {
            let actualEndDays = 0;
            if (actualEndActgPeriod.period == endActgPeriod.period) {
                actualEndDays = Period.daysBetween(
                    actualEndActgPeriod.startDate,
                    actualServiceEndDate,
                    true,
                    endDateInclusive
                );
            } else {
                actualEndDays = p.actgPeriod.numberOfDays(p.actgPeriod.period);
            }

            let perDayRevenue = 0;
            if (actualEndDays !== 0)
                perDayRevenue = p.planAmount / actualEndDays;

            const newRevenueDays = Period.daysBetween(
                endActgPeriod.startDate,
                endDate,
                true,
                endDateInclusive
            );

            totalNewPlanAmount += perDayRevenue * newRevenueDays;

            break;
        } else {
            totalNewPlanAmount += p.planAmount;
        }
    }

    return totalPlanAmount - totalNewPlanAmount;
}

export function buildPlanByType(
    planType,
    orgConfig,
    startDate,
    endDate,
    originalBooking,
    calendarConfig,
    term,
    salesOrder,
    revenueArrangementItem
) {
    const d2 = orgConfig.find(
        (config) => config.id === "properties/ratable_end_date_inclusive"
    );
    const endDateInclusive = (d2 && d2.value) || false;

    let plan;
    if (planType === "monthly_endmonth_exclusive") {
        plan = FullMonthRatablePlan.buildPlan(
            startDate,
            endDate,
            originalBooking,
            calendarConfig
        );
    } else if (planType === "fixed") {
        const d3 = orgConfig.find(
            (config) => config.id === "properties/ratable_start_exact_days"
        );
        const exactDays = (d3 && d3.value) || false;

        if (exactDays) {
            plan = Modified30_360Plan.buildPlan(
                startDate,
                endDate,
                originalBooking,
                endDateInclusive,
                calendarConfig,
                term
            );
        } else {
            plan = FixedMonthRatablePlan.buildPlan(
                startDate,
                endDate,
                originalBooking,
                endDateInclusive,
                exactDays,
                calendarConfig
            );
        }
    } else if (planType === "daily") {
        plan = DailyRatablePlan.buildPlan(
            startDate,
            endDate,
            originalBooking,
            endDateInclusive,
            calendarConfig
        );
    } else if (planType === "true30/360") {
        let startDateInclusive = true;
        const sdi = orgConfig.find(
            (config) =>
                config.id === "properties/true30_360/start_date_inclusive"
        );

        if (sdi && sdi.value !== undefined) {
            startDateInclusive = sdi.value;
        }

        const drhp = orgConfig.find(
            (config) => config.id === "properties/dereference_rule_has_priority"
        );
        const dereferenceRuleHasPriority = (drhp && drhp.value) || false;
        let setEndDate = false;
        const originalEndDate = endDate;

        if (
            startDateInclusive == true &&
            dereferenceRuleHasPriority == true &&
            term !== undefined
        ) {
            try {
                // If this is a multi-item order so that there are individual
                // items that chain together for a larger total term then we
                // will get the next item in the chain from ours and calculate
                // our end date by taking the next item's start date and removing
                // one day.
                const raItem = revenueArrangementItem.salesOrderItem;

                // First filter by product.
                const items = salesOrder.salesOrderItem.filter(
                    (item) => item.productId == raItem.productId
                );

                // Then sort by start date.
                items.sort((a, b) => {
                    return a.startDate.localeCompare(b.startDate);
                });

                // Find this item in the list of items.
                let myItemIndex = -1;

                for (let i = 0; i < items.length; i++) {
                    if (items[i] == raItem || _.isEqual(items[i], raItem)) {
                        myItemIndex = i;
                        break;
                    }
                }

                if (myItemIndex != -1) {
                    // Found the item in the list so see if there is a next item
                    // in this contract chain.
                    let index = myItemIndex + 1;

                    while (index < items.length) {
                        const item = items[index];

                        if (
                            item &&
                            item.startDate &&
                            Period.isAfter(item.startDate, startDate) &&
                            item.salePrice > 0
                        ) {
                            // This means there is an item in our contract term
                            // chain after the one we are processing that isn't
                            // a credit note so use it to set the end date for ours
                            // to a day prior to the next item's start date.
                            setEndDate = true;
                            endDate = Period.addDays(item.startDate, -1);
                            break;
                        }

                        index++;
                    }

                    if (!setEndDate) {
                        // Ok in this case this item is the last one in the
                        // contract term chain.
                        const startComps = Period.toComponents(startDate);

                        if (parseInt(startComps.month) == 2) {
                            // If it starts in February we have to set the
                            // end date so it matches the others.
                            endDate = Period.addMonths(
                                Period.addDays(startDate, -1),
                                term
                            );
                            setEndDate = true;
                        }
                    }
                }

                if (!setEndDate) {
                    // Use the term (in months) to calculate the end date as we
                    // will ignore the end date passed to us since the dereference
                    // rule has priority configuration is set.  This is to ensure
                    // that we are calculating by 30 day months.
                    endDate = Period.calculateEndDateBasedOnTrue30DayTerm(
                        startDate,
                        term
                    );
                }
            } catch (e) {
                console.log(
                    `Unexpected error while recalculating end date: `,
                    e
                );
            }
        }

        //console.log(">>>> Set End Date: ", salesOrder.id, setEndDate, originalEndDate, "=>", endDate)
        plan = True30_360Plan.buildPlan(
            startDate,
            endDate,
            originalBooking,
            startDateInclusive,
            calendarConfig,
            salesOrder,
            setEndDate
        );
    } else {
        plan = ApportionedRatablePlan.buildPlan(
            startDate,
            endDate,
            originalBooking,
            endDateInclusive,
            calendarConfig
        );
    }

    return plan;
}

export function buildPlan(
    revenueArrangementItem,
    salesOrder,
    orgConfig,
    productRevenue,
    calendarConfig,
    customCode
) {
    let startDate = revenueArrangementItem.deliveryStartDate;

    let endDate =
        revenueArrangementItem.extendedDeliveryEndDate ||
        revenueArrangementItem.deliveryEndDate;
    let originalBooking = productRevenue;
    // delayed revenue is true our plan is 0 (in modification this will be adjustment)

    const d1 = orgConfig.find(
        (config) => config.id === "properties/ratable_plan_type"
    );
    const planType = (d1 && d1.value) || "default";

    const customPlanConfig = orgConfig.find(
        (config) => config.id === "properties/custom-code/plan-name"
    );

    const customPlanName = customPlanConfig && customPlanConfig.value;

    const d2 = orgConfig.find(
        (config) => config.id === "properties/ratable_end_date_inclusive"
    );
    const endDateInclusive = (d2 && d2.value) || false;

    const d3 = orgConfig.find(
        (cfg) => cfg.id === "properties/max-contract-years"
    );
    const maxContractYears = (d3 && d3.value && Number(d3.value)) || 20;

    if (
        calendarConfig &&
        calendarConfig.type === "retail" &&
        calendarConfig.weekGrouping !== "Group444"
    ) {
        if (planType !== "daily")
            throw new Error(
                "Retail calendar can only be used with a daily plan!"
            );
    }

    let plan;
    if (planType === "custom") {
        const buildPlan = customCode[customPlanName];
        // let's call custom plans
        plan = buildPlan(
            startDate,
            endDate,
            originalBooking,
            endDateInclusive,
            calendarConfig,
            revenueArrangementItem
        );

        assertValidPlan(plan);
    } else {
        let term;
        if (revenueArrangementItem.salesOrderItem) {
            term = revenueArrangementItem.salesOrderItem.term;
        }
        plan = buildPlanByType(
            planType,
            orgConfig,
            startDate,
            endDate,
            originalBooking,
            calendarConfig,
            term,
            salesOrder,
            revenueArrangementItem
        );
    }

    plan =
        plan.length > 12 * maxContractYears
            ? plan.slice(0, 12 * maxContractYears)
            : plan;
    //do not support more than maxContractYears years

    assertValidPlan(plan);

    return plan;
}

export function prefillZeroAtStart(plan, salesOrder, calendarConfig) {
    if (plan.length === 0) return;

    const firstPlanActgPeriod = plan[0].actgPeriod;

    const arrangementEffectivePeriod = Period.toActgPeriod(
        salesOrder.revenueArrangement[0].effectiveDate,
        calendarConfig
    );

    if (arrangementEffectivePeriod.period >= firstPlanActgPeriod.period) return;

    const periods = Period.periodsBetween(
        arrangementEffectivePeriod.period,
        firstPlanActgPeriod.period,
        true,
        false,
        calendarConfig || { type: "default" }
    );

    if (periods.length === 0) return;

    let toAdd = [];
    let actgPeriod = firstPlanActgPeriod;
    for (let period of periods) {
        actgPeriod = actgPeriod.get(period);

        toAdd.push({
            actgPeriod,
            planAmount: 0
        });
    }

    plan.unshift(...toAdd);

    assertValidPlan(plan);
}

export function calculatePlanAmount(itemPlan, productRevenue, baseRAI) {
    if (!baseRAI.plan) {
        return [];
    }
    const baseItemProductRevenue = baseRAI.productRevenue;
    let basePlan = {},
        newPlan = [];
    baseRAI.plan.forEach((plan) => {
        basePlan[plan.actgPeriod.period] = plan;
    });
    for (let plan of itemPlan) {
        const { planAmount } = basePlan[plan.actgPeriod.period];
        plan["planAmount"] =
            (planAmount / baseItemProductRevenue) * productRevenue;
        newPlan.push(plan);
    }
    return newPlan;
}
